/**
 * @voltxampere/web — Thermal preferences (Sprint 34-5)
 *
 * AYARLAR > Termal sub-section toggle for the Sprint 34-3 canvas
 * heatmap overlay. Bridges `thermalEnabledSignal` to the IDB
 * settings store via `SettingsRecord.thermal` additive field — no
 * DB version bump per Sprint 30 v2 strategy.
 *
 * Pattern mirrors `live-mode-prefs.ts` (Sprint 33-4): single
 * `initThermalPrefs()` boot path hydrate, fire-and-forget
 * `mergeSettings()` on every flip.
 *
 * Default: enabled = true. Hydrate uses `?? true` so v4 settings
 * records (no thermal field) start with overlay enabled.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/** Master toggle for the thermal canvas heatmap overlay. */
export const thermalEnabledSignal = signal<boolean>(true);

let initialised = false;

/**
 * Hydrate the thermal-enabled signal from IDB. Called once during
 * boot. Returns 1 when the field hydrated, 0 when defaulted.
 */
export async function initThermalPrefs(): Promise<number> {
  if (initialised) return 0;
  initialised = true;
  try {
    const settings = await getSettings();
    const thermal = settings?.thermal;
    if (thermal && typeof thermal.enabled === "boolean") {
      thermalEnabledSignal.value = thermal.enabled;
      return 1;
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA thermal-prefs] hydrate failed:", err);
    }
  }
  return 0;
}

function persist(): void {
  void mergeSettings({
    thermal: { enabled: thermalEnabledSignal.value },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA thermal-prefs] persist failed:", err);
    }
  });
}

/** Toggle the thermal overlay (persists immediately). */
export function setThermalEnabled(enabled: boolean): void {
  thermalEnabledSignal.value = enabled;
  persist();
}
